/**
 * State/region subdivisions for the countries offered in the profile address
 * form (apps/web/src/app/me/page.tsx). Static constant per country, same
 * pattern as `languages.ts` — these lists are bounded by the countries we
 * actually offer, not a general-purpose world-subdivision dataset, so a
 * runtime dependency buys nothing here.
 *
 * Add a country's entry here whenever a country is added to that form's
 * `countries` list, or the dropdown silently falls back to Australia's.
 */

export const REGIONS_BY_COUNTRY: Readonly<Record<string, readonly string[]>> = {
  Australia: [
    "Australian Capital Territory",
    "New South Wales",
    "Northern Territory",
    "Queensland",
    "South Australia",
    "Tasmania",
    "Victoria",
    "Western Australia",
  ],
  "New Zealand": [
    "Auckland",
    "Bay of Plenty",
    "Canterbury",
    "Gisborne",
    "Hawke's Bay",
    "Manawatu-Whanganui",
    "Marlborough",
    "Nelson",
    "Northland",
    "Otago",
    "Southland",
    "Taranaki",
    "Tasman",
    "Waikato",
    "Wellington",
    "West Coast",
  ],
  "United States": [
    "Alabama",
    "Alaska",
    "Arizona",
    "Arkansas",
    "California",
    "Colorado",
    "Connecticut",
    "Delaware",
    "District of Columbia",
    "Florida",
    "Georgia",
    "Hawaii",
    "Idaho",
    "Illinois",
    "Indiana",
    "Iowa",
    "Kansas",
    "Kentucky",
    "Louisiana",
    "Maine",
    "Maryland",
    "Massachusetts",
    "Michigan",
    "Minnesota",
    "Mississippi",
    "Missouri",
    "Montana",
    "Nebraska",
    "Nevada",
    "New Hampshire",
    "New Jersey",
    "New Mexico",
    "New York",
    "North Carolina",
    "North Dakota",
    "Ohio",
    "Oklahoma",
    "Oregon",
    "Pennsylvania",
    "Rhode Island",
    "South Carolina",
    "South Dakota",
    "Tennessee",
    "Texas",
    "Utah",
    "Vermont",
    "Virginia",
    "Washington",
    "West Virginia",
    "Wisconsin",
    "Wyoming",
  ],
  "United Kingdom": ["England", "Scotland", "Wales", "Northern Ireland"],
  Canada: [
    "Alberta",
    "British Columbia",
    "Manitoba",
    "New Brunswick",
    "Newfoundland and Labrador",
    "Northwest Territories",
    "Nova Scotia",
    "Nunavut",
    "Ontario",
    "Prince Edward Island",
    "Quebec",
    "Saskatchewan",
    "Yukon",
  ],
} as const

/** Regions for `country`, or an empty list when the country isn't in the map. */
export function getRegionsForCountry(country: string): readonly string[] {
  return REGIONS_BY_COUNTRY[country] ?? []
}

/**
 * #1392 — every region across every offered country, flattened and
 * deduplicated. Used where a surface needs to recognise or list a region
 * regardless of which country it belongs to (the event-discovery filter's
 * state list, the match location-search picker) rather than for a single
 * country's dropdown.
 */
export const ALL_REGIONS: readonly string[] = Array.from(
  new Set(Object.values(REGIONS_BY_COUNTRY).flat()),
)

const COUNTRY_BY_REGION: ReadonlyMap<string, string> = new Map(
  Object.entries(REGIONS_BY_COUNTRY).flatMap(([country, regions]) =>
    regions.map((region) => [region, country] as const),
  ),
)

/**
 * The offered country a region belongs to, or null if it isn't one of
 * `ALL_REGIONS`. No region name is reused across the countries this app
 * offers (verified by the dedup in `ALL_REGIONS` never dropping an entry),
 * so this lookup is unambiguous.
 */
export function countryForRegion(region: string): string | null {
  return COUNTRY_BY_REGION.get(region) ?? null
}
